/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { CheckboxCheckedFilled, Checkbox, ChevronDown, ArrowRight, Error } from '@carbon/icons-react';
import Link from '../Link/Link.js';
import deprecate from '../../prop-types/deprecate.js';
import { composeEventHandlers } from '../../tools/events.js';
import { usePrefix } from '../../internal/usePrefix.js';
import useIsomorphicEffect from '../../internal/useIsomorphicEffect.js';
import { getInteractiveContent, getRoleContent } from '../../internal/useNoInteractiveChildren.js';
import { useMergedRefs } from '../../internal/useMergedRefs.js';
import { useFeatureFlag } from '../FeatureFlags/index.js';
import { useId } from '../../internal/useId.js';
import '../Text/index.js';
import { Text } from '../Text/Text.js';
import { matches } from '../../internal/keyboard/match.js';
import { Enter, Space } from '../../internal/keyboard/keys.js';

var _rect, _path, _CheckboxCheckedFille, _Checkbox, _ChevronDown, _ChevronDown2;
const Tile = /*#__PURE__*/React__default.forwardRef(function Tile(_ref, ref) {
  let {
    children,
    className,
    light = false,
    slug,
    hasRoundedCorners = false,
    ...rest
  } = _ref;
  const prefix = usePrefix();
  const tileClasses = cx(`${prefix}--tile`, {
    [`${prefix}--tile--light`]: light,
    [`${prefix}--tile--slug`]: slug,
    [`${prefix}--tile--slug-rounded`]: slug && hasRoundedCorners
  }, className);
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: tileClasses,
    ref: ref
  }, rest), children, slug);
});
Tile.displayName = 'Tile';
Tile.propTypes = {
  /**
   * The child nodes.
   */
  children: PropTypes.node,
  /**
   * The CSS class names.
   */
  className: PropTypes.string,
  /**
   * **Experimental**: Specify if the `Tile` component should be rendered with rounded corners. Only valid
   * when `slug` prop is present
   */
  hasRoundedCorners: PropTypes.bool,
  /**
   * `true` to use the light version. For use on $ui-01 backgrounds only.
   * Don't use this to make tile background color same as container background color.
   *
   * @deprecated
   */
  light: deprecate(PropTypes.bool, 'The `light` prop for `Tile` is no longer needed and has been deprecated. It will be removed in the next major release. Use the Layer component instead.'),
  /**
   * **Experimental**: Provide a `Slug` component to be rendered inside the `Tile` component
   */
  slug: PropTypes.node
};
const ClickableTile = /*#__PURE__*/React__default.forwardRef(function ClickableTile(_ref2, ref) {
  let {
    children,
    className,
    clicked = false,
    disabled,
    href,
    light,
    onClick = () => {},
    onKeyDown = () => {},
    renderIcon: Icon,
    hasRoundedCorners,
    slug,
    ...rest
  } = _ref2;
  const prefix = usePrefix();
  const classes = cx(`${prefix}--tile`, `${prefix}--tile--clickable`, {
    [`${prefix}--tile--is-clicked`]: clicked,
    [`${prefix}--tile--light`]: light,
    [`${prefix}--tile--slug`]: slug,
    [`${prefix}--tile--slug-rounded`]: slug && hasRoundedCorners
  }, className);
  const [isSelected, setIsSelected] = useState(clicked);
  function handleOnClick(evt) {
    evt?.persist?.();
    setIsSelected(!isSelected);
    onClick(evt);
  }
  function handleOnKeyDown(evt) {
    evt?.persist?.();
    if (matches(evt, [Enter, Space])) {
      setIsSelected(!isSelected);
    }
    onKeyDown(evt);
  }

  // To Do: Replace with an an icon from `@carbon/react`
  // since the hollow slug in `ClickableTile` is not interactive
  const hollowSlugIcon = /*#__PURE__*/React__default.createElement("svg", {
    className: `${prefix}--tile--slug-icon`,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, _rect || (_rect = /*#__PURE__*/React__default.createElement("rect", {
    x: "0.5",
    y: "0.5",
    width: "23",
    height: "23"
  })), _path || (_path = /*#__PURE__*/React__default.createElement("path", {
    d: "M13.2436 16H11.5996L10.9276 13.864H7.95164L7.29164 16H5.68364L8.49164 7.624H10.4596L13.2436 16ZM10.5436 12.508L9.46364 9.064H9.40364L8.33564 12.508H10.5436ZM17.9341 16H14.1301V14.728H15.2341V8.896H14.1301V7.624H17.9341V8.896H16.8181V14.728H17.9341V16Z",
    fill: "#161616"
  })));
  const v12DefaultIcons = useFeatureFlag('enable-v12-tile-default-icons');
  if (v12DefaultIcons) {
    if (!Icon) {
      Icon = ArrowRight;
    }
    if (disabled) {
      Icon = Error;
    }
  }
  const iconClasses = cx({
    [`${prefix}--tile--icon`]: !v12DefaultIcons || v12DefaultIcons && !disabled,
    [`${prefix}--tile--disabled-icon`]: v12DefaultIcons && disabled
  });
  return /*#__PURE__*/React__default.createElement(Link, _extends({
    className: classes,
    href: href,
    tabIndex: !href && !disabled ? 0 : undefined,
    onClick: !disabled ? handleOnClick : undefined,
    onKeyDown: handleOnKeyDown,
    ref: ref,
    disabled: disabled
  }, rest), slug ? /*#__PURE__*/React__default.createElement("div", {
    className: `${prefix}--tile-content`
  }, children) : children, slug && hollowSlugIcon, Icon && /*#__PURE__*/React__default.createElement(Icon, {
    className: iconClasses,
    "aria-hidden": "true"
  }));
});
ClickableTile.displayName = 'ClickableTile';
ClickableTile.propTypes = {
  /**
   * The child nodes.
   */
  children: PropTypes.node,
  /**
   * The CSS class names.
   */
  className: PropTypes.string,
  /**
   * Boolean for whether a tile has been clicked.
   */
  clicked: PropTypes.bool,
  /**
   * Specify whether the ClickableTile should be disabled
   */
  disabled: PropTypes.bool,
  /**
   * **Experimental**: Specify if the `ClickableTile` component should be rendered with rounded corners.
   * Only valid when `slug` prop is present
   */
  hasRoundedCorners: PropTypes.bool,
  /**
   * The href for the link.
   */
  href: PropTypes.string,
  /**
   * `true` to use the light version. For use on $ui-01 backgrounds only.
   * Don't use this to make tile background color same as container background color.
   */
  light: deprecate(PropTypes.bool, 'The `light` prop for `ClickableTile` is no longer needed and has been deprecated. It will be removed in the next major release. Use the Layer component instead.'),
  /**
   * Specify the function to run when the ClickableTile is clicked
   */
  onClick: PropTypes.func,
  /**
   * Specify the function to run when the ClickableTile is interacted with via a keyboard
   */
  onKeyDown: PropTypes.func,
  /**
   * The rel property for the link.
   */
  rel: PropTypes.string,
  /**
   * Optional prop to allow overriding the icon rendering.
   * Can be a React component class
   */
  // @ts-expect-error: Invalid derived prop type, seemingly no real solution.
  renderIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};
const SelectableTile = /*#__PURE__*/React__default.forwardRef(function SelectableTile(_ref3, ref) {
  let {
    children,
    className,
    disabled,
    id,
    light,
    onClick = () => {},
    onChange = () => {},
    onKeyDown = () => {},
    selected = false,
    tabIndex = 0,
    title = 'title',
    slug,
    hasRoundedCorners,
    ...rest
  } = _ref3;
  const prefix = usePrefix();
  const clickHandler = onClick;
  const keyDownHandler = onKeyDown;
  const [isSelected, setIsSelected] = useState(selected);
  const [prevSelected, setPrevSelected] = useState(selected);
  const classes = cx(`${prefix}--tile`, `${prefix}--tile--selectable`, {
    [`${prefix}--tile--is-selected`]: isSelected,
    [`${prefix}--tile--light`]: light,
    [`${prefix}--tile--disabled`]: disabled,
    [`${prefix}--tile--slug`]: slug,
    [`${prefix}--tile--slug-rounded`]: slug && hasRoundedCorners
  }, className);

  // TODO: rename to handleClick when handleClick prop is deprecated
  function handleOnClick(evt) {
    evt.preventDefault();
    evt?.persist?.();
    if (slug && slugRef.current && slugRef.current.contains(evt.target)) {
      return;
    }
    setIsSelected(!isSelected);
    clickHandler(evt);
    onChange(evt, isSelected, id);
  }

  // TODO: rename to handleKeyDown when handleKeyDown prop is deprecated
  function handleOnKeyDown(evt) {
    evt?.persist?.();
    if (matches(evt, [Enter, Space])) {
      evt.preventDefault();
      setIsSelected(!isSelected);
      onChange(evt, isSelected, id);
    }
    keyDownHandler(evt);
  }
  function handleChange(event) {
    setIsSelected(event.target.checked);
    onChange(event, isSelected, id);
  }
  if (selected !== prevSelected) {
    setIsSelected(selected);
    setPrevSelected(selected);
  }

  // Slug is always size `xs`
  const slugRef = useRef(null);
  let normalizedSlug;
  if (slug && slug['type']?.displayName === 'Slug') {
    normalizedSlug = /*#__PURE__*/React__default.cloneElement(slug, {
      size: 'xs',
      ref: slugRef
    });
  }
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    React__default.createElement("div", _extends({
      className: classes,
      onClick: !disabled ? handleOnClick : undefined,
      role: "checkbox",
      "aria-checked": isSelected,
      onKeyDown: !disabled ? handleOnKeyDown : undefined
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      ,
      tabIndex: !disabled ? tabIndex : undefined,
      ref: ref,
      id: id,
      onChange: !disabled ? handleChange : undefined,
      title: title
    }, rest), /*#__PURE__*/React__default.createElement("span", {
      className: `${prefix}--tile__checkmark ${prefix}--tile__checkmark--persistent`
    }, isSelected ? _CheckboxCheckedFille || (_CheckboxCheckedFille = /*#__PURE__*/React__default.createElement(CheckboxCheckedFilled, null)) : _Checkbox || (_Checkbox = /*#__PURE__*/React__default.createElement(Checkbox, null))), /*#__PURE__*/React__default.createElement(Text, {
      as: "label",
      htmlFor: id,
      className: `${prefix}--tile-content`
    }, children), normalizedSlug)
  );
});
SelectableTile.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * Specify whether the SelectableTile should be disabled
   */
  disabled: PropTypes.bool,
  /**
   * **Experimental**: Specify if the `SelectableTile` component should be rendered with rounded corners.
   * Only valid when `slug` prop is present
   */
  hasRoundedCorners: PropTypes.bool,
  /**
   * The ID of the `<input>`.
   */
  id: PropTypes.string,
  /**
   * `true` to use the light version. For use on $ui-01 backgrounds only.
   * Don't use this to make tile background color same as container background color.
   */
  light: deprecate(PropTypes.bool, 'The `light` prop for `SelectableTile` is no longer needed and has been deprecated. It will be removed in the next major release. Use the Layer component instead.'),
  /**
   * The `name` of the `<input>`.
   * @deprecated
   */
  name: PropTypes.string,
  /**
   * The empty handler of the `<input>`.
   */
  onChange: PropTypes.func,
  /**
   * Specify the function to run when the SelectableTile is clicked
   */
  onClick: PropTypes.func,
  /**
   * Specify the function to run when the SelectableTile is interacted with via a keyboard
   */
  onKeyDown: PropTypes.func,
  /**
   * `true` to select this tile.
   */
  selected: PropTypes.bool,
  /**
   * **Experimental**: Provide a `Slug` component to be rendered inside the `SelectableTile` component
   */
  slug: PropTypes.node,
  /**
   * Specify the tab index of the wrapper element
   */
  tabIndex: PropTypes.number,
  /**
   * The `title` of the `<input>`.
   */
  title: PropTypes.string,
  /**
   * The value of the `<input>`.
   * @deprecated
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
const ExpandableTile = /*#__PURE__*/React__default.forwardRef(function ExpandableTile(_ref4, forwardRef) {
  let {
    tabIndex = 0,
    className,
    children,
    expanded = false,
    tileMaxHeight = 0,
    // eslint-disable-line
    tilePadding = 0,
    // eslint-disable-line
    onClick,
    onKeyUp,
    tileCollapsedIconText = 'Interact to expand Tile',
    tileExpandedIconText = 'Interact to collapse Tile',
    tileCollapsedLabel,
    tileExpandedLabel,
    light,
    slug,
    hasRoundedCorners,
    ...rest
  } = _ref4;
  const [isTileMaxHeight, setIsTileMaxHeight] = useState(tileMaxHeight);
  const [isTilePadding, setIsTilePadding] = useState(tilePadding);
  const [prevExpanded, setPrevExpanded] = useState(expanded);
  const [prevTileMaxHeight, setPrevTileMaxHeight] = useState(tileMaxHeight);
  const [prevTilePadding, setPrevTilePadding] = useState(tilePadding);
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [interactive, setInteractive] = useState(true);
  const aboveTheFold = useRef(null);
  const belowTheFold = useRef(null);
  const tileContent = useRef(null);
  const tile = useRef(null);
  const ref = useMergedRefs([forwardRef, tile]);
  const prefix = usePrefix();
  if (expanded !== prevExpanded) {
    setIsExpanded(expanded);
    setPrevExpanded(expanded);
    setMaxHeight();
  }
  if (tileMaxHeight !== prevTileMaxHeight) {
    setIsTileMaxHeight(tileMaxHeight);
    setPrevTileMaxHeight(tileMaxHeight);
  }
  if (tilePadding !== prevTilePadding) {
    setIsTilePadding(tilePadding);
    setPrevTilePadding(tilePadding);
  }
  function setMaxHeight() {
    if (isExpanded && tileContent.current) {
      setIsTileMaxHeight(tileContent.current.getBoundingClientRect()?.height);
    }
    if (aboveTheFold.current) {
      setIsTileMaxHeight(aboveTheFold.current.getBoundingClientRect().height);
    }
  }
  function handleClick(evt) {
    evt?.persist?.();
    setIsExpanded(!isExpanded);
    setMaxHeight();
  }
  function handleKeyUp(evt) {
    if (evt.target !== tile.current) {
      if (matches(evt, [Enter, Space])) {
        evt.preventDefault();
      }
    }
  }
  function getChildren() {
    return React__default.Children.toArray(children);
  }
  const classNames = cx(`${prefix}--tile`, `${prefix}--tile--expandable`, {
    [`${prefix}--tile--is-expanded`]: isExpanded,
    [`${prefix}--tile--light`]: light
  }, className);
  const interactiveClassNames = cx(`${prefix}--tile`, `${prefix}--tile--expandable`, `${prefix}--tile--expandable--interactive`, {
    [`${prefix}--tile--is-expanded`]: isExpanded,
    [`${prefix}--tile--light`]: light,
    [`${prefix}--tile--slug`]: slug,
    [`${prefix}--tile--slug-rounded`]: slug && hasRoundedCorners
  }, className);
  const chevronInteractiveClassNames = cx(`${prefix}--tile__chevron`, `${prefix}--tile__chevron--interactive`);
  const childrenAsArray = getChildren();
  useIsomorphicEffect(() => {
    if (!tile.current || !aboveTheFold.current) {
      return;
    }
    const getStyle = window.getComputedStyle(tile.current, null);
    const {
      current: node
    } = aboveTheFold;
    const {
      height
    } = node.getBoundingClientRect();
    const paddingTop = parseInt(getStyle.getPropertyValue('padding-top'), 10);
    const paddingBottom = parseInt(getStyle.getPropertyValue('padding-bottom'), 10);
    setIsTileMaxHeight(height);
    setIsTilePadding(paddingTop + paddingBottom);
  }, [isTileMaxHeight]);
  useIsomorphicEffect(() => {
    if (!aboveTheFold.current || !belowTheFold.current) {
      return;
    }

    // Interactive elements or elements that are given a role should be treated
    // the same because elements with a role can not be rendered inside a `button`
    if (!getInteractiveContent(belowTheFold.current) && !getRoleContent(belowTheFold.current) && !getInteractiveContent(aboveTheFold.current) && !getRoleContent(aboveTheFold.current) && !slug) {
      setInteractive(false);
    }
  }, [slug]);
  useIsomorphicEffect(() => {
    if (!tile.current) {
      return;
    }
    if (isExpanded) {
      tile.current.style.maxHeight = '';
    } else {
      tile.current.style.maxHeight = isTileMaxHeight + isTilePadding + 'px';
    }
  }, [isExpanded, isTileMaxHeight, isTilePadding]);
  useEffect(() => {
    if (!aboveTheFold.current) {
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      const [aboveTheFold] = entries;
      setIsTileMaxHeight(aboveTheFold.contentRect.height);
    });
    resizeObserver.observe(aboveTheFold.current);
    return () => resizeObserver.disconnect();
  }, []);
  const belowTheFoldId = useId('expandable-tile-interactive');

  // Slug is always size `xs`
  let normalizedSlug;
  if (slug && slug['type']?.displayName === 'Slug') {
    normalizedSlug = /*#__PURE__*/React__default.cloneElement(slug, {
      size: 'xs'
    });
  }
  return interactive ? /*#__PURE__*/React__default.createElement("div", _extends({
    // @ts-expect-error: Needlesly strict & deep typing for the element type
    ref: ref,
    className: interactiveClassNames
  }, rest), /*#__PURE__*/React__default.createElement("div", {
    ref: tileContent
  }, normalizedSlug, /*#__PURE__*/React__default.createElement("div", {
    ref: aboveTheFold,
    className: `${prefix}--tile-content`
  }, childrenAsArray[0]), /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    "aria-expanded": isExpanded,
    "aria-controls": belowTheFoldId,
    onKeyUp: composeEventHandlers([onKeyUp, handleKeyUp]),
    onClick: composeEventHandlers([onClick, handleClick]),
    "aria-label": isExpanded ? tileExpandedIconText : tileCollapsedIconText,
    className: chevronInteractiveClassNames
  }, _ChevronDown || (_ChevronDown = /*#__PURE__*/React__default.createElement(ChevronDown, null))), /*#__PURE__*/React__default.createElement("div", {
    ref: belowTheFold,
    className: `${prefix}--tile-content`,
    id: belowTheFoldId
  }, childrenAsArray[1]))) : /*#__PURE__*/React__default.createElement("button", _extends({
    type: "button"
    // @ts-expect-error: Needlesly strict & deep typing for the element type
    ,
    ref: ref,
    className: classNames,
    "aria-expanded": isExpanded,
    title: isExpanded ? tileExpandedIconText : tileCollapsedIconText
  }, rest, {
    onKeyUp: composeEventHandlers([onKeyUp, handleKeyUp]),
    onClick: composeEventHandlers([onClick, handleClick]),
    tabIndex: tabIndex
  }), /*#__PURE__*/React__default.createElement("div", {
    ref: tileContent
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: aboveTheFold,
    className: `${prefix}--tile-content`
  }, childrenAsArray[0]), /*#__PURE__*/React__default.createElement("div", {
    className: `${prefix}--tile__chevron`
  }, /*#__PURE__*/React__default.createElement("span", null, isExpanded ? tileExpandedLabel : tileCollapsedLabel), _ChevronDown2 || (_ChevronDown2 = /*#__PURE__*/React__default.createElement(ChevronDown, null))), /*#__PURE__*/React__default.createElement("div", {
    ref: belowTheFold,
    className: `${prefix}--tile-content`
  }, childrenAsArray[1])));
});
ExpandableTile.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * `true` if the tile is expanded.
   */
  expanded: PropTypes.bool,
  /**
   * Specify if the `ExpandableTile` component should be rendered with rounded corners.
   * Only valid when `slug` prop is present
   */
  hasRoundedCorners: PropTypes.bool,
  /**
   * An ID that can be provided to aria-labelledby
   */
  id: PropTypes.string,
  /**
   * `true` to use the light version. For use on $ui-01 backgrounds only.
   * Don't use this to make tile background color same as container background color.
   */
  light: deprecate(PropTypes.bool, 'The `light` prop for `ExpandableTile` is no longer needed and has been deprecated. It will be removed in the next major release. Use the Layer component instead.'),
  /**
   * Specify the function to run when the ExpandableTile is clicked
   */
  onClick: PropTypes.func,
  /**
   * optional handler to trigger a function when a key is pressed
   */
  onKeyUp: PropTypes.func,
  /**
   * **Experimental**: Provide a `Slug` component to be rendered inside the `ExpandableTile` component
   */
  slug: PropTypes.node,
  /**
   * The `tabindex` attribute.
   */
  tabIndex: PropTypes.number,
  /**
   * The description of the "collapsed" icon that can be read by screen readers.
   */
  tileCollapsedIconText: PropTypes.string,
  /**
   * When "collapsed", a label to appear next to the chevron (e.g., "View more").
   */
  tileCollapsedLabel: PropTypes.string,
  /**
   * The description of the "expanded" icon that can be read by screen readers.
   */
  tileExpandedIconText: PropTypes.string,
  /**
   * When "expanded", a label to appear next to the chevron (e.g., "View less").
   */
  tileExpandedLabel: PropTypes.string
};
ExpandableTile.displayName = 'ExpandableTile';
const TileAboveTheFoldContent = /*#__PURE__*/React__default.forwardRef(function TilAboveTheFoldContent(_ref5, ref) {
  let {
    children
  } = _ref5;
  const prefix = usePrefix();
  return /*#__PURE__*/React__default.createElement("div", {
    ref: ref,
    className: `${prefix}--tile-content__above-the-fold`
  }, children);
});
TileAboveTheFoldContent.propTypes = {
  /**
   * The child nodes.
   */
  children: PropTypes.node
};
TileAboveTheFoldContent.displayName = 'TileAboveTheFoldContent';
const TileBelowTheFoldContent = /*#__PURE__*/React__default.forwardRef(function TileBelowTheFoldContent(_ref6, ref) {
  let {
    children
  } = _ref6;
  const prefix = usePrefix();
  return /*#__PURE__*/React__default.createElement("div", {
    ref: ref,
    className: `${prefix}--tile-content__below-the-fold`
  }, children);
});
TileBelowTheFoldContent.propTypes = {
  /**
   * The child nodes.
   */
  children: PropTypes.node
};
TileBelowTheFoldContent.displayName = 'TileBelowTheFoldContent';

export { ClickableTile, ExpandableTile, SelectableTile, Tile, TileAboveTheFoldContent, TileBelowTheFoldContent };
